import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    getAllCVDrafts,
    deleteCVDraft,
    generateCVPDFFromDraft,
    generateCVDOCXFromDraft,
    duplicateCVDraft,
    downloadOriginalCV,
} from "@/lib/recruitmentApi";
import { toast } from "react-hot-toast";
import ConfirmationModal from "@/components/ui/ConfirmationModal";
import UnifiedCvImportModal from "@/components/admin/recruitment/UnifiedCvImportModal";
import CvPreviewModal from "@/components/admin/recruitment/CvPreviewModal";
import {
    PencilIcon,
    TrashIcon,
    DocumentArrowDownIcon,
    PlusIcon,
    CheckCircleIcon,
    ClockIcon,
    DocumentTextIcon,
    DocumentDuplicateIcon,
    ArrowUpTrayIcon,
    EyeIcon,
} from "@heroicons/react/24/outline";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const CvLibraryPage = () => {
    const [drafts, setDrafts] = useState([]);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [isLoading, setIsLoading] = useState(true);
    const [draftToDelete, setDraftToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [previewDraft, setPreviewDraft] = useState(null);
    const navigate = useNavigate();

    const filteredDrafts = drafts.filter(
        (draft) =>
            (statusFilter === "all" || draft.status === statusFilter) &&
            `${draft.candidate_name || ""} ${draft.title || ""} ${draft.trigram || ""}`
                .toLocaleLowerCase()
                .includes(search.toLocaleLowerCase())
    );

    const fetchDrafts = async () => {
        setIsLoading(true);
        try {
            const data = await getAllCVDrafts();
            setDrafts(data);
        } catch (error) {
            console.error("Error fetching drafts", error);
            toast.error("Erreur lors de la récupération de la bibliothèque");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchDrafts();
    }, []);

    useEffect(() => {
        if (typeof window !== "undefined") {
            const params = new URLSearchParams(window.location.search);
            if (params.get("import") === "1") {
                setIsImportModalOpen(true);
            }
        }
    }, []);

    const handleImportSuccess = async () => {
        toast.success("CV importé avec succès dans la bibliothèque !");
        await fetchDrafts();
    };

    const handleDelete = (id) => {
        setDraftToDelete(id);
    };

    const confirmDelete = async () => {
        if (!draftToDelete) return;
        setDeleting(true);
        try {
            await deleteCVDraft(draftToDelete);
            setDrafts((prev) => prev.filter((d) => d.id !== draftToDelete));
            toast.success("CV supprimé");
            setDraftToDelete(null);
        } catch {
            toast.error("Erreur lors de la suppression");
        } finally {
            setDeleting(false);
        }
    };

    const handleDuplicate = async (id) => {
        try {
            toast.loading("Duplication...", { id: "dup" });
            await duplicateCVDraft(id);
            toast.success("CV dupliqué", { id: "dup" });
            fetchDrafts();
        } catch {
            toast.error("Erreur lors de la duplication", { id: "dup" });
        }
    };

    const handleGenerate = async (draft, format = "pdf") => {
        const isDocx = format === "docx";
        const extension = isDocx ? "docx" : "pdf";
        const mimeType = isDocx
            ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            : "application/pdf";

        try {
            toast.loading(`Génération du ${isDocx ? "Word" : "PDF"}...`, { id: "gen" });
            const blob = isDocx
                ? await generateCVDOCXFromDraft(draft.id)
                : await generateCVPDFFromDraft(draft.id);
            const url = window.URL.createObjectURL(new Blob([blob], { type: mimeType }));
            const link = document.createElement("a");
            link.href = url;
            const sanitizedName = draft.candidate_name?.trim().replace(/\s+/g, "_") || "Lenvia";
            const fileName = `${sanitizedName}_CV_Lenvia.${extension}`;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.setTimeout(() => {
                window.URL.revokeObjectURL(url);
            }, 0);
            toast.success(`CV ${isDocx ? "Word" : "PDF"} généré`, { id: "gen" });
            fetchDrafts(); // Refresh to see updated status/date
        } catch {
            toast.error(`Erreur lors de la génération ${isDocx ? "Word" : "PDF"}`, { id: "gen" });
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case "generated":
                return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
            case "ready":
                return <CheckCircleIcon className="w-5 h-5 text-blue-500" />;
            default:
                return <ClockIcon className="w-5 h-5 text-slate-400" />;
        }
    };

    return (
        <div className="bg-transparent min-h-screen p-1 sm:p-6 min-w-0" data-testid="cv-library-root">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col sm:flex-row gap-4 justify-between sm:items-center mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-white">Bibliothèque de CV</h1>
                        <p className="text-slate-400 mt-1">
                            Retrouvez vos CV importés, brouillons et documents générés.
                        </p>
                    </div>

                    <div className="flex flex-wrap gap-3">
                        <button
                            type="button"
                            onClick={() => setIsImportModalOpen(true)}
                            className="inline-flex items-center min-h-11 px-4 py-2 bg-lenvia-blue hover:bg-blue-600 text-white rounded-lg transition-all font-semibold text-xs cursor-pointer shadow-md"
                            data-testid="cv-library-import-button"
                        >
                            <ArrowUpTrayIcon className="w-4 h-4 mr-2" />
                            Importer un CV
                        </button>
                        <Link
                            to="/admin/recrutement/cv-builder?new=1&manual=1"
                            className="inline-flex items-center min-h-11 px-4 py-2 border border-white/20 text-white rounded-lg hover:bg-white/10 transition-all font-semibold text-xs"
                            data-testid="action-create-cv-button"
                        >
                            <PlusIcon className="w-4 h-4 mr-2" />
                            + Créer un CV
                        </Link>
                    </div>
                </div>

                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                    <input
                        type="search"
                        aria-label="Rechercher un CV"
                        placeholder="Rechercher par nom, titre ou trigramme..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="flex-1 bg-slate-900 border border-white/10 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-lenvia-blue"
                    />
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="bg-slate-900 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-lenvia-blue"
                    >
                        <option value="all">Tous les statuts</option>
                        <option value="draft">Brouillon</option>
                        <option value="ready">Prêt</option>
                        <option value="generated">Généré</option>
                    </select>
                </div>

                {/* Drafts List */}
                {isLoading ? (
                    <div className="text-center py-12 text-slate-400">
                        Chargement des CVs...
                    </div>
                ) : filteredDrafts.length === 0 ? (
                    <div className="text-center py-12 border border-dashed border-white/10 rounded-xl">
                        <DocumentTextIcon className="w-12 h-12 text-slate-500 mx-auto mb-3" />
                        <h3 className="text-lg font-medium text-white mb-1">Aucun CV trouvé</h3>
                        <p className="text-slate-400 text-sm mb-4">
                            Commencez par créer votre premier profil candidat ou importez un document.
                        </p>
                        <button
                            type="button"
                            onClick={() => setIsImportModalOpen(true)}
                            className="inline-flex items-center px-4 py-2 bg-lenvia-blue text-white rounded-lg hover:bg-blue-600 transition-colors"
                        >
                            <ArrowUpTrayIcon className="w-5 h-5 mr-2" />
                            Importer un CV
                        </button>
                    </div>
                ) : (
                    <div className="bg-slate-900/50 border border-white/10 rounded-xl overflow-hidden overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b border-white/10 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                                    <th className="px-6 py-4">Candidat</th>
                                    <th className="px-6 py-4">Statut</th>
                                    <th className="px-6 py-4">Complétude</th>
                                    <th className="px-6 py-4">Dernière modif.</th>
                                    <th className="px-6 py-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/10 text-sm">
                                {filteredDrafts.map((draft) => (
                                    <tr key={draft.id} className="hover:bg-white/[0.02] transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center space-x-3">
                                                <div className="w-10 h-10 rounded-full bg-lenvia-blue/20 flex items-center justify-center text-lenvia-blue font-bold">
                                                    {draft.trigram || draft.candidate_name?.substring(0, 2).toUpperCase() || "??"}
                                                </div>
                                                <div>
                                                    <Link
                                                        to={`/admin/recrutement/cv-builder/${draft.id}`}
                                                        className="font-semibold text-white hover:text-lenvia-blue transition-colors"
                                                    >
                                                        {draft.candidate_name || "Sans nom"}
                                                    </Link>
                                                    <div className="text-xs text-slate-400">
                                                        {draft.title || "Titre non renseigné"}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center space-x-2">
                                                {getStatusIcon(draft.status)}
                                                <span className="capitalize text-slate-300">
                                                    {draft.status === "generated"
                                                        ? "Généré"
                                                        : draft.status === "ready"
                                                        ? "Prêt"
                                                        : "Brouillon"}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center space-x-2">
                                                <div className="w-24 bg-slate-800 rounded-full h-2">
                                                    <div
                                                        className="bg-lenvia-blue h-2 rounded-full"
                                                        style={{ width: `${draft.completion_score || 0}%` }}
                                                    />
                                                </div>
                                                <span className="text-xs text-slate-400">
                                                    {draft.completion_score || 0}%
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-xs text-slate-400">
                                            {draft.updated_at
                                                ? format(new Date(draft.updated_at), "dd MMM yyyy HH:mm", { locale: fr })
                                                : "—"}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end space-x-2">
                                                <button
                                                    onClick={() => setPreviewDraft(draft)}
                                                    className="p-2 text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                                                    title="Aperçu du CV"
                                                    data-testid={`preview-cv-${draft.id}`}
                                                >
                                                    <EyeIcon className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => navigate(`/admin/recrutement/cv-builder/${draft.id}`)}
                                                    className="p-2 text-slate-400 hover:text-lenvia-blue hover:bg-lenvia-blue/10 rounded-lg transition-colors"
                                                    title="Modifier"
                                                >
                                                    <PencilIcon className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleGenerate(draft, "pdf")}
                                                    className="p-2 text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10 rounded-lg transition-colors"
                                                    title="Télécharger PDF"
                                                >
                                                    <DocumentArrowDownIcon className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleGenerate(draft, "docx")}
                                                    className="p-2 text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10 rounded-lg transition-colors"
                                                    title="Télécharger Word"
                                                >
                                                    <DocumentTextIcon className="w-4 h-4" />
                                                </button>
                                                {draft.has_original && (
                                                    <button
                                                        title="Télécharger le CV original"
                                                        onClick={() =>
                                                            downloadOriginalCV(draft.id).catch(() =>
                                                                toast.error("Document original indisponible")
                                                            )
                                                        }
                                                        className="px-2 py-1 text-xs text-slate-300 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg"
                                                    >
                                                        Original
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => handleDuplicate(draft.id)}
                                                    className="p-2 text-slate-400 hover:text-amber-400 hover:bg-amber-500/10 rounded-lg transition-colors"
                                                    title="Dupliquer"
                                                >
                                                    <DocumentDuplicateIcon className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(draft.id)}
                                                    className="p-2 text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition-colors"
                                                    title="Supprimer"
                                                >
                                                    <TrashIcon className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <ConfirmationModal
                isOpen={Boolean(draftToDelete)}
                onClose={() => setDraftToDelete(null)}
                title="Supprimer le CV"
                message="Êtes-vous sûr de vouloir supprimer définitivement ce document de la bibliothèque ?"
                confirmText="Supprimer définitivement"
                variant="danger"
                isSubmitting={deleting}
                onConfirm={confirmDelete}
                testId="delete-cv-modal"
            />

            {/* Parcours d'import unifié (Lot 1) */}
            <UnifiedCvImportModal
                isOpen={isImportModalOpen}
                onClose={() => setIsImportModalOpen(false)}
                onSuccess={handleImportSuccess}
                onOpenBuilder={(draftId) => navigate(`/admin/recrutement/cv-builder/${draftId}`)}
            />

            {/* Modal de prévisualisation directe haute fidélité */}
            {previewDraft && (
                <CvPreviewModal
                    isOpen={Boolean(previewDraft)}
                    onClose={() => setPreviewDraft(null)}
                    draftId={previewDraft.id}
                    candidateName={previewDraft.candidate_name}
                    jobTitle={previewDraft.title}
                />
            )}
        </div>
    );
};

export default CvLibraryPage;
